import { useState, useRef, useEffect } from "react";
import { useTranslation } from "../i18n";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import { isDividerEntry } from "../utils/taxonomyMap";
import { RAD, FS, Z } from "../lib/tokens";

// Phase 10 — per-party-per-case roles.
//
// defaultRolesForJunction(entries, junctionType)
//   Returns the party_role codes whose meta.defaultForJunctions includes the
//   given junction-type key (D4). Used to pre-select roles when a party chip
//   is freshly added. Junction-type keys: client, opponent, additionalOpponent,
//   opponentLawyer, expert, mediator, arbiter.
export function defaultRolesForJunction(entries: any, junctionType: any) {
  if (!Array.isArray(entries) || !junctionType) return [];
  return entries
    .filter(
      (e) =>
        e &&
        e.meta &&
        Array.isArray(e.meta.defaultForJunctions) &&
        e.meta.defaultForJunctions.includes(junctionType),
    )
    .map((e) => e.code);
}

// RolePicker — a compact multi-select for a single party's roles. Renders the
// current role labels as small tags plus a click-to-open dropdown listing all
// 12 party_role codes (D3: unfiltered — every code always shown). Multi-select
// (D2). An empty selection is flagged in red so the D1 "at least one role"
// requirement is visible before the save fails server-side.
type RolePickerProps = {
  roles?: any[];
  onChange: (next: any[]) => void;
  entries?: any[];
  junctionType?: string;
  disabled?: boolean;
};

export function RolePicker({
  roles,
  onChange,
  entries,
  junctionType,
  disabled,
}: RolePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: any) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const cur = Array.isArray(roles) ? roles : [];
  const list = Array.isArray(entries) ? entries : [];
  /* BRIEF 108 §2a — THE D3 FLIP IS REVERSED (ruled by Laurent, 2026-08-20,
     recorded in RULINGS.md): the picker offers EVERY non-archived party_role
     entry, on every junction, in both views. The meta.validForJunctions
     filter that stood here made prod's dropdown show 6-of-15 for clients and
     2-of-15 for opponents — data-driven, but the data was the restriction.
     The entries feed is already non-archived (useTaxonomy("party_role")
     default filters archived, useTaxonomy.ts:209 — verified at the App
     mount, partyRoleTax) and already in taxonomy order (the hook sorts).
     `junctionType` still arrives from every call site and still matters at
     ADD time — defaultRolesForJunction above reads meta.defaultForJunctions
     for pre-selection, untouched — it just no longer narrows the list. */
  const opts = list;
  const toggle = (code: string) => {
    const next = cur.includes(code)
      ? cur.filter((c) => c !== code)
      : [...cur, code];
    onChange(next);
  };
  // The role display itself is click-to-open (not only the small "roles"
  // button) so clicking anywhere on a party's role area opens the picker.
  const openProps = disabled
    ? {}
    : { onClick: () => setOpen((v) => !v), style: { cursor: "pointer" } };
  return (
    <span
      ref={ref}
      style={{
        position: "relative",
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
      }}
    >
      {cur.length === 0 ? (
        // Role is optional — no red "required" alarm on an empty selection.
        // Neutral, muted affordance: "+ role" opens the dropdown on click
        // (via openProps); read-only chips show a plain "no role" instead.
        <span
          {...openProps}
          title={disabled ? undefined : t("party.role.add_tooltip", { fallback: "Add the role in the procedure" })}
          style={{
            fontSize: FS.s8,
            color: "var(--tm)",
            fontStyle: "italic",
            ...(openProps.style || {}),
          }}
        >
          {disabled
            ? t("party.role.none", { fallback: "no role" })
            : t("party.role.add", { fallback: "+ role" })}
        </span>
      ) : (
        cur.map((code) => (
          <span
            key={code}
            {...openProps}
            style={{
              fontSize: FS.s8,
              background: "var(--bi)",
              color: "var(--ts)",
              borderRadius: RAD.r3,
              padding: "0 3px",
              lineHeight: "13px",
              ...(openProps.style || {}),
            }}
          >
            {resolveTaxonomyLabel(code, list)}
          </span>
        ))
      )}
      {!disabled && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            fontSize: FS.s8,
            padding: "0 4px",
            height: 14,
            lineHeight: "12px",
            borderRadius: RAD.r3,
            background: "rgba(74,124,255,.15)",
            color: "var(--ac)",
            border: "1px solid rgba(74,124,255,.3)",
            cursor: "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
          }}
        >
          {t("party.role.edit", { fallback: "roles" })}
        </button>
      )}
      {open && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: Z.modal2,
            marginTop: 2,
            minWidth: 150,
            background: "var(--bc)",
            border: "1px solid var(--br)",
            borderRadius: RAD.r6,
            boxShadow: "0 6px 20px rgba(0,0,0,.7)",
            overflow: "hidden",
          }}
        >
          {opts.length === 0 && (
            <div
              style={{
                padding: "6px 10px",
                fontSize: FS.s10,
                color: "var(--tm)",
                fontStyle: "italic",
              }}
            >
              {t("common.no_match", { fallback: "No match" })}
            </div>
          )}
          {opts.map((e) =>
            /* BRIEF 108 §6b — a divider draws as a rule element: never
               toggleable, never a role. */
            isDividerEntry(e) ? (
              <div
                key={e.code}
                aria-hidden="true"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 8px",
                  color: "var(--tm)",
                  fontSize: FS.s9_5,
                  pointerEvents: "none",
                }}
              >
                <span style={{ flex: 1, borderTop: "1px solid var(--br)" }} />
                {e.label ? <span>{resolveTaxonomyLabel(e.code, list)}</span> : null}
                <span style={{ flex: 1, borderTop: "1px solid var(--br)" }} />
              </div>
            ) : (
              <div
                key={e.code}
                onMouseDown={() => toggle(e.code)}
                className="menu-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: FS.s10_5,
                }}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={cur.includes(e.code)}
                  style={{ pointerEvents: "none", margin: 0 }}
                />
                <span>{resolveTaxonomyLabel(e.code, list)}</span>
              </div>
            ),
          )}
        </div>
      )}
    </span>
  );
}

export default RolePicker;
